import React from 'react';
import { Clock, Shield, Mail } from 'lucide-react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { Button, Card } from './UIComponents';

interface AccessPendingProps {
  userEmail?: string;
}

export const AccessPending: React.FC<AccessPendingProps> = ({ userEmail }) => {
  const { signOut } = useAuth();
  const { user } = useUser();
  const email = userEmail || user?.primaryEmailAddress?.emailAddress || 'your account';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="max-w-md w-full text-center">
        <div className="mb-6">
          <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
            <Clock className="w-8 h-8 text-amber-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Access Pending</h1>
          <p className="text-slate-600">
            Your account has been created but you don't have access to any customers yet.
          </p>
        </div>

        <div className="bg-slate-50 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-3 text-left">
            <Mail className="w-5 h-5 text-slate-400 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-slate-700">Signed in as</p>
              <p className="text-sm text-slate-500">{email}</p>
            </div>
          </div>
        </div>

        <div className="space-y-4 text-left mb-6">
          <h2 className="font-semibold text-slate-900 flex items-center gap-2">
            <Shield className="w-4 h-4 text-accent" />
            What happens next?
          </h2>
          <ul className="text-sm text-slate-600 space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-accent font-bold">1.</span>
              <span>A portal administrator has been notified of your account.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-accent font-bold">2.</span>
              <span>They will assign you a role and grant access to the appropriate customers.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-accent font-bold">3.</span>
              <span>Once approved, you'll be able to view and manage customer information.</span>
            </li>
          </ul>
        </div>

        <div className="pt-4 border-t border-slate-200">
          <p className="text-xs text-slate-500 mb-4">
            If you believe this is an error, please contact your administrator.
          </p>
          <Button
            variant="secondary"
            onClick={() => signOut()}
            className="w-full"
          >
            Sign Out
          </Button>
        </div>
      </Card>
    </div>
  );
};
